import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

// Session timeout in milliseconds (30 minutes)
const SESSION_TIMEOUT = 30 * 60 * 1000;

// Backend API URL
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Check session on mount
  useEffect(() => {
    const checkSession = () => {
      const sessionData = sessionStorage.getItem('abc_session');
      if (sessionData) {
        const { authenticated, timestamp } = JSON.parse(sessionData);
        const timeSinceActivity = Date.now() - timestamp;
        
        if (authenticated && timeSinceActivity < SESSION_TIMEOUT) {
          setIsAuthenticated(true);
          setLastActivity(timestamp);
        } else {
          // Session expired
          sessionStorage.removeItem('abc_session');
          setIsAuthenticated(false);
        }
      }
      setIsLoading(false);
    };
    
    checkSession();
  }, []);

  // Update activity timestamp on user interaction
  const updateActivity = useCallback(() => {
    if (isAuthenticated) {
      const now = Date.now();
      setLastActivity(now);
      sessionStorage.setItem('abc_session', JSON.stringify({
        authenticated: true,
        timestamp: now
      }));
    }
  }, [isAuthenticated]);

  // Check for session timeout
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkTimeout = () => {
      const timeSinceActivity = Date.now() - lastActivity;
      if (timeSinceActivity >= SESSION_TIMEOUT) {
        logout();
      }
    };

    const interval = setInterval(checkTimeout, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [isAuthenticated, lastActivity]);

  // Track user activity
  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    const handleActivity = () => updateActivity();

    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated, updateActivity]);

  const login = async (username, password) => {
    try {
      // Validate credentials via backend API
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        const now = Date.now();
        setIsAuthenticated(true);
        setLastActivity(now);
        sessionStorage.setItem('abc_session', JSON.stringify({
          authenticated: true,
          timestamp: now
        }));
        return { success: true };
      }
      return { success: false, error: data.error || 'invalid_credentials' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'network_error' };
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('abc_session');
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      isLoading, 
      login, 
      logout,
      updateActivity 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
